import React from 'react';
import './EditGemeenschapModal.css';
import { etappeOptions, bisdomOptions } from '../config/formOptions';

/**
 * EditGemeenschapModal Component
 * 
 * A modal dialog for adding new or editing existing gemeenschap items
 * 
 * @param {boolean} isOpen - Controls modal visibility
 * @param {Function} onClose - Callback when modal should close
 * @param {Function} onSubmit - Callback when form is submitted
 * @param {boolean} isEditMode - Whether the modal is in edit mode
 * @param {Object} formData - Form data state
 * @param {Function} onChange - Handler for form field changes
 * @param {Object} formErrors - Validation errors
 * @param {Array} availableParochies - Available parochies for selected bisdom
 * @param {Function} onDelete - Callback when delete button is clicked (edit mode only)
 */
const EditGemeenschapModal = ({
  isOpen,
  onClose,
  onSubmit,
  isEditMode,
  formData,
  onChange,
  formErrors,
  availableParochies = [],
  onDelete
}) => {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{isEditMode ? 'Gemeenschap bewerken' : 'Nieuwe gemeenschap'}</h3>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>
        
        <form onSubmit={onSubmit} className="modal-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="bisdom">Bisdom <span className="required">*</span></label>
              <select
                id="bisdom"
                name="bisdom"
                value={formData.bisdom}
                onChange={onChange}
                className={formErrors.bisdom ? 'error' : ''}
              >
                <option value="">Selecteer bisdom</option>
                {bisdomOptions.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
              {formErrors.bisdom && <span className="error-message">{formErrors.bisdom}</span>}
            </div>
            
            <div className="form-group">
              <label htmlFor="parochie">Parochie<span className="required">*</span></label>
              <select
                id="parochie"
                name="parochie"
                value={formData.parochie}
                onChange={onChange}
                disabled={!formData.bisdom}
                className={formErrors.parochie ? 'error' : ''}
              >
                <option value="">{!formData.bisdom ? 'Selecteer eerst bisdom' : 'Selecteer parochie'}</option>
                {availableParochies.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
              {formErrors.parochie && <span className="error-message">{formErrors.parochie}</span>}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="gemeenschap">Gemeenschap naam<span className="required">*</span></label>
            <input
              type="text"
              id="gemeenschap"
              name="gemeenschap"
              value={formData.gemeenschap}
              onChange={onChange}
              placeholder="Naam van de gemeenschap - bijv. '1ste Gemeenschap'"
              className={formErrors.gemeenschap ? 'error' : ''}
            />
            {formErrors.gemeenschap && <span className="error-message">{formErrors.gemeenschap}</span>}
          </div>

          <div className="verantwoordelijke-group">
            <div className="form-row">
              <div className="form-group">
                <label>Verantwoordelijke</label>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="verantwoordelijkeVoornaam">Voornaam(en)</label>
                <input
                  type="text"
                  id="verantwoordelijkeVoornaam"
                  name="verantwoordelijkeVoornaam"
                  value={formData.verantwoordelijkeVoornaam}
                  onChange={onChange}
                  placeholder="'Peter' of 'Peter & Marie'"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="verantwoordelijkeAchternaam">Achternaam</label>
                <input
                  type="text"
                  id="verantwoordelijkeAchternaam"
                  name="verantwoordelijkeAchternaam"
                  value={formData.verantwoordelijkeAchternaam}
                  onChange={onChange}
                  placeholder="Familienaam"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="verantwoordelijkeEmail">Email adres</label>
                <input
                  type="email"
                  id="verantwoordelijkeEmail"
                  name="verantwoordelijkeEmail"
                  value={formData.verantwoordelijkeEmail}
                  onChange={onChange}
                  placeholder="email@voorbeeld.nl"
                  className={formErrors.verantwoordelijkeEmail ? 'error' : ''}
                />
                {formErrors.verantwoordelijkeEmail && <span className="error-message">{formErrors.verantwoordelijkeEmail}</span>}
              </div>
            </div>
          </div>

          <div className="catechist-group">
            <div className="form-row">
              <div className="form-group">
                <label>Catechist</label>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="catechistVoornaam">Voornaam</label>
                <input
                  type="text"
                  id="catechistVoornaam"
                  name="catechistVoornaam"
                  value={formData.catechistVoornaam}
                  onChange={onChange}
                  placeholder="Voornaam"
                />
              </div>

              <div className="form-group">
                <label htmlFor="catechistAchternaam">Achternaam</label>
                <input
                  type="text"
                  id="catechistAchternaam"
                  name="catechistAchternaam"
                  value={formData.catechistAchternaam}
                  onChange={onChange}
                  placeholder="Familienaam"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="catechistEmail">Email adres</label>
                <input
                  type="email"
                  id="catechistEmail"
                  name="catechistEmail"
                  value={formData.catechistEmail}
                  onChange={onChange}
                  placeholder="email@voorbeeld.nl"
                  className={formErrors.catechistEmail ? 'error' : ''}
                />
                {formErrors.catechistEmail && <span className="error-message">{formErrors.catechistEmail}</span>}
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="laatsteEtappe">
                Laatste etappe <span className="required">*</span>
              </label>
              <select
                id="laatsteEtappe"
                name="laatsteEtappe"
                value={formData.laatsteEtappe}
                onChange={onChange}
                className={formErrors.laatsteEtappe ? 'error' : ''}
              >
                <option value="">Selecteer etappe</option>
                {etappeOptions.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
              {formErrors.laatsteEtappe && <span className="error-message">{formErrors.laatsteEtappe}</span>}
            </div>
            
            <div className="form-group">
              <label htmlFor="laatsteEtappeAfgesloten">
                Is de laatste etappe afgesloten?
              </label>
              <div className="toggle-container">
                <span className="toggle-label">{formData.laatsteEtappeAfgesloten ? 'Afgesloten' : 'Nog open'}</span>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    id="laatsteEtappeAfgesloten"
                    name="laatsteEtappeAfgesloten"
                    role="switch"
                    aria-label="Is de laatste etappe afgesloten?"
                    aria-checked={formData.laatsteEtappeAfgesloten}
                    checked={formData.laatsteEtappeAfgesloten}
                    onChange={onChange}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="aantalLeden">Aantal leden (manueel)</label>
            <input
              type="number"
              id="aantalLeden"
              name="aantalLeden"
              min="0"
              step="1"
              value={formData.aantalLeden ?? ''}
              onChange={onChange}
              placeholder="Bijv. 12"
              className={formErrors.aantalLeden ? 'error' : ''}
            />
            {formErrors.aantalLeden && <span className="error-message">{formErrors.aantalLeden}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="opmerkingen">Opmerkingen</label>
            <textarea
              id="opmerkingen"
              name="opmerkingen"
              value={formData.opmerkingen}
              onChange={onChange}
              rows="3"
              placeholder="Eventuele opmerkingen..."
            />
          </div>

          <div className="modal-footer">
            <button type="button" className="cancel-button" onClick={onClose}>
              Annuleren
            </button>
            {isEditMode && (
              <button 
                type="button" 
                className="delete-button"
                onClick={onDelete}
              >
                Verwijderen
              </button>
            )}
            <button type="submit" className="submit-button">
              {isEditMode ? 'Opslaan' : 'Toevoegen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditGemeenschapModal;
